import { create } from 'zustand';
import { deleteAudioFile, loadAudioFileData, saveAudioFile } from '../utils/indexedDB';
import { beginWorkKeyCapture, carryWorkKey, computeWorkKey, getCarriedWorkKey, getCurrentWorkKey, getWorkDuration, rememberWorkDuration, setCurrentWorkKey } from '../lib/workKey';

const PENDING_FILE_ID = '__pending_tool_file__';
const PENDING_META_KEY = '__pending_tool_meta__';
// Keep new writes distinct from IDs restored from an earlier page load.
let generation = Date.now();
// Consuming a handoff invalidates its persistence, but it is still the same
// song for telemetry. Only a new selection supersedes this separate counter.
let workGeneration = 0;

/** Where a region-send came from, so tools can write results back in place. */
export interface PendingOriginRegion {
  regionId: string;
  trackId: string;
  /** Song-time position (seconds) of the region on the playlist. */
  position: number;
  /** Region length in seconds at send time. */
  length: number;
  /** Source-offset trim (Region.start, seconds) at send time. Lets write-back
   *  verify the region is unedited — a left-trimmed region (start > 0) is
   *  still writable because extraction sliced exactly [start, start+length].
   *  Optional: metadata persisted before this field existed reads as 0. */
  start?: number;
}

/** Extra hand-off flags for multi-tool chains (e.g. the assistant's polish flow). */
export interface PendingFileChainOptions {
  /** Original input identity, preserved when a tool hands off processed audio. */
  workKey?: string | null;
  /** Decoded source duration, when the sending panel already knows it. */
  durationSec?: number;
  /** Assistant request correlation, preserved across chained tools/reloads. */
  requestId?: string;
  assistantAction?: string;
  /** Cover handoffs belong to the project generation and cover that sent them. */
  projectGeneration?: number;
  coverId?: number | null;
  /** Input provenance for journey analytics; never infer it from a filename. */
  inputSource?: 'demo' | 'upload';
  /** Tools still to run after the receiving tool finishes (e.g. ['autotune']). */
  chain?: string[] | null;
  /** Receiving tool should apply automatically with its default/natural settings. */
  autoApply?: boolean;
}

interface PendingFileState {
  workKey?: string;
  inputSource: 'demo' | 'upload';
  file: File | null;
  fileId: string | null;
  targetTool: string | null;
  sourceTool: string | null;
  muteVocals: boolean;
  originRegion: PendingOriginRegion | null;
  chain: string[] | null;
  autoApply: boolean;
  requestId: string | null;
  assistantAction: string | null;
  projectGeneration: number | null;
  coverId: number | null;
  setFile: (file: File, tool: string, muteVocals?: boolean, sourceTool?: string, originRegion?: PendingOriginRegion | null, chainOpts?: PendingFileChainOptions) => Promise<void>;
  setDecodedDuration: (file: File, durationSec: number) => void;
  hydrate: () => Promise<void>;
  /** An expected ID prevents stale receivers from clearing a newer handoff. */
  consume: (expectedFileId?: string | null) => void;
}

export const usePendingFileStore = create<PendingFileState>((set, get) => ({
  inputSource: 'upload',
  file: null,
  fileId: null,
  targetTool: null,
  sourceTool: null,
  muteVocals: false,
  originRegion: null,
  chain: null,
  autoApply: false,
  requestId: null,
  assistantAction: null,
  projectGeneration: null,
  coverId: null,

  setFile: async (file, tool, muteVocals = false, sourceTool = tool, originRegion = null, chainOpts = {}) => {
    carryWorkKey(file, chainOpts.workKey);
    const workKey = getCarriedWorkKey(file);
    if (chainOpts.durationSec !== undefined) rememberWorkDuration(file, chainOpts.durationSec);
    const gen = ++generation;
    const workGen = ++workGeneration;
    const workCapture = beginWorkKeyCapture(() => workGen === workGeneration);
    try { setCurrentWorkKey(null); } catch { /* best effort */ }
    const fileId = `${PENDING_FILE_ID}-${gen}`;
    const chain = chainOpts.chain?.length ? chainOpts.chain : null;
    const autoApply = chainOpts.autoApply ?? false;
    const inputSource = chainOpts.inputSource ?? 'upload';
    const requestId = chainOpts.requestId ?? null;
    const assistantAction = chainOpts.assistantAction ?? null;
    const projectGeneration = chainOpts.projectGeneration ?? null;
    const coverId = chainOpts.coverId ?? null;
    set({ file, fileId, workKey, targetTool: tool, sourceTool, muteVocals, originRegion, chain, autoApply, inputSource, requestId, assistantAction, projectGeneration, coverId });

    // Preserve the synchronous handoff above. Hashing must neither delay the
    // visible file nor overwrite a receiving tool's duration-aware work key.
    try {
      const key = await computeWorkKey(file);
      if (workCapture.isCurrent() && getCurrentWorkKey() === null) setCurrentWorkKey(key, false);
    } catch { /* Measuring a work must never prevent a handoff. */ }

    // Persistence (IndexedDB + sessionStorage) only matters for reload /
    // hydrate. If it fails (quota, private mode, IDB unavailable) we still
    // have the in-memory store above, which is all same-tab navigation needs
    // — so never throw from here.
    try {
      const data = await file.arrayBuffer();
      if (gen !== generation) return;
      await saveAudioFile({
        id: fileId,
        sessionId: '__pending__',
        name: file.name,
        data,
        sampleRate: 0,
        channels: 0,
        length: 0,
        duration: 0,
      });

      // An already-mounted panel may consume the handoff during this write.
      // Do not resurrect it in sessionStorage after processing has begun.
      if (gen !== generation) {
        void deleteAudioFile(fileId).catch(() => {});
        return;
      }

      sessionStorage.setItem(PENDING_META_KEY, JSON.stringify({
        fileId,
        targetTool: tool,
        sourceTool,
        muteVocals,
        originRegion,
        chain,
        autoApply,
        inputSource,
        requestId,
        assistantAction,
        projectGeneration,
        coverId,
        durationSec: getWorkDuration(file),
        workKey,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
      }));
    } catch (e) {
      console.warn('[pendingFileStore] Failed to persist pending file; continuing in-memory only', e);
    }
  },

  setDecodedDuration: (file, durationSec) => {
    rememberWorkDuration(file, durationSec);
    if (get().file !== file) return;
    try {
      const raw = sessionStorage.getItem(PENDING_META_KEY);
      const meta = raw ? JSON.parse(raw) : null;
      if (meta?.fileId === get().fileId) sessionStorage.setItem(PENDING_META_KEY, JSON.stringify({ ...meta, durationSec }));
    } catch { /* The in-memory metadata still works. */ }
  },

  hydrate: async () => {
    if (get().file) return;
    const gen = generation;
    const workCapture = beginWorkKeyCapture(() => gen === generation);
    let metaRaw: string | null;
    try { metaRaw = sessionStorage.getItem(PENDING_META_KEY); }
    catch { return; }
    if (!metaRaw) return;

    let meta: {
      fileId?: string;
      targetTool: string;
      sourceTool?: string;
      muteVocals: boolean;
      originRegion?: PendingOriginRegion | null;
      chain?: string[] | null;
      autoApply?: boolean;
      inputSource?: 'demo' | 'upload';
      requestId?: string;
      assistantAction?: string;
      projectGeneration?: number;
      coverId?: number;
      durationSec?: number;
      workKey?: string;
      name: string;
      type: string;
      lastModified: number;
    };
    try {
      meta = JSON.parse(metaRaw);
    } catch (e) {
      console.warn('[pendingFileStore] Failed to parse pending file metadata; discarding', e);
      sessionStorage.removeItem(PENDING_META_KEY);
      return;
    }

    let pending: Awaited<ReturnType<typeof loadAudioFileData>>;
    const fileId = meta.fileId ?? PENDING_FILE_ID;
    try {
      pending = await loadAudioFileData(fileId);
      if (!workCapture.isCurrent()) return;
    } catch (e) {
      console.warn('[pendingFileStore] Failed to load pending file from IndexedDB', e);
      return;
    }
    if (!pending) return;
    if (get().file || sessionStorage.getItem(PENDING_META_KEY) !== metaRaw) return;

    const file = new File([pending.data], meta.name, {
      type: meta.type || 'audio/*',
      lastModified: meta.lastModified || Date.now(),
    });

    if (meta.durationSec !== undefined) rememberWorkDuration(file, meta.durationSec);
    carryWorkKey(file, meta.workKey);
    const workGen = ++workGeneration;
    try {
      const key = await computeWorkKey(file);
      if (!workCapture.isCurrent() || workGen !== workGeneration || get().file) return;
      setCurrentWorkKey(key, false);
    } catch { /* Reloaded handoffs still work without telemetry. */ }
    if (!workCapture.isCurrent() || workGen !== workGeneration || get().file) return;

    set({
      file,
      fileId,
      workKey: getCarriedWorkKey(file),
      targetTool: meta.targetTool,
      sourceTool: meta.sourceTool || meta.targetTool,
      muteVocals: meta.muteVocals,
      originRegion: meta.originRegion ?? null,
      chain: meta.chain?.length ? meta.chain : null,
      autoApply: meta.autoApply ?? false,
      inputSource: meta.inputSource === 'demo' ? 'demo' : 'upload',
      requestId: typeof meta.requestId === 'string' ? meta.requestId : null,
      assistantAction: typeof meta.assistantAction === 'string' ? meta.assistantAction : null,
      projectGeneration: typeof meta.projectGeneration === 'number' ? meta.projectGeneration : null,
      coverId: typeof meta.coverId === 'number' ? meta.coverId : null,
    });
  },

  consume: (expectedFileId) => {
    const fileId = get().fileId;
    if (expectedFileId !== undefined && expectedFileId !== fileId) return;
    generation++;
    set({ file: null, fileId: null, workKey: undefined, targetTool: null, sourceTool: null, muteVocals: false, originRegion: null, chain: null, autoApply: false, inputSource: 'upload', requestId: null, assistantAction: null, projectGeneration: null, coverId: null });
    try { sessionStorage.removeItem(PENDING_META_KEY); } catch { /* In-memory handoffs still work. */ }
    if (fileId) void deleteAudioFile(fileId).catch(() => {});
  },
}));
